import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  MoreVertical,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileEdit,
  Eye,
  Image as ImageIcon,
  Video,
  Heart,
  MessageCircle,
  Share2,
  Calendar as CalendarIcon,
  Grid3X3,
  List,
  BarChart3,
  Star,
  Settings,
  Send,
  UserPlus,
  Users,
  Globe,
  ArrowUpRight,
  ToggleLeft,
  ToggleRight,
  Bell,
  Hash,
  Copy,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { postsAPI, socialAPI, analyticsAPI } from '../../services/api';
import { useThemeStore } from '../../store/themeStore';
import type { Theme } from '../../store/themeStore';
import { PLATFORMS } from '../composer/types';
import type { UrlMedia } from '../composer/types';
import { PostPreview } from '../composer/PostPreview';
import { AccountAvatar } from '../common/AccountAvatar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PostItem {
  id: string;
  title?: string;
  caption?: string;
  text?: string;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  platforms: string[];
  media_urls?: string[];
  media_count?: number;
  scheduled_at: string | null;
  published_at: string | null;
  created_at: string;
  start?: string;
  content_type?: string;
  accounts?: string[];
  total_likes?: number;
  total_comments?: number;
  total_shares?: number;
  total_reach?: number;
  is_favorite?: boolean;
}

interface AnalyticsOverview {
  total_posts: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_reach: number;
  engagement_rate: number;
  trends?: {
    likes_change?: number;
    comments_change?: number;
    shares_change?: number;
    reach_change?: number;
  };
}

interface PlatformDistItem {
  platform: string;
  count: number;
  percentage: number;
}

interface ContentPerfItem {
  content_type: string;
  total_posts: number;
  avg_likes: number;
  avg_comments: number;
  avg_shares: number;
  avg_reach: number;
  engagement_rate: number;
}

interface AnalyticsPost {
  id: string;
  text: string;
  platforms: string[];
  status: string;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_reach: number;
  published_at: string | null;
  media_thumbnail?: string;
}

interface FeedPost {
  id: string;
  text: string;
  status: string;
  target_platforms: string[];
  target_accounts?: string[];
  scheduled_at: string | null;
  published_at: string | null;
  created_at: string;
  media_count: number;
  media_thumbnail: string | null;
  total_likes: number;
  total_comments: number;
  is_favorite?: boolean;
  publish_results?: Record<string, unknown>;
}

interface SocialAccount {
  id: string;
  platform: string;
  username: string;
  display_name: string;
  avatar_url: string;
  is_active: boolean;
}

type CalendarView = 'day' | 'week' | 'month';
type ContentFilter = 'all' | 'posts' | 'stories' | 'videos' | 'reels';
type CenterTab = 'posting' | 'feed' | 'favorites' | 'analytics' | 'settings';
type RightTab = 'preview' | 'comments' | 'activity';
type AccountFilter = 'all' | 'my' | 'manager';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  twitter: '#000000',
  tiktok: '#010101',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  pinterest: '#E60023',
  threads: '#000000',
  bluesky: '#0085FF',
};

// SVG-based platform icons that render cleanly at all sizes
const PlatformIcon: React.FC<{ platform: string; size?: number; className?: string }> = ({ platform, size = 14, className = '' }) => {
  const s = size;
  switch (platform) {
    case 'instagram':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    case 'twitter':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
    case 'tiktok':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.82a8.18 8.18 0 004.77 1.53V6.88a4.84 4.84 0 01-1-.19z"/>
        </svg>
      );
    case 'linkedin':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      );
    case 'youtube':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    case 'pinterest':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/>
        </svg>
      );
    case 'threads':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.773.776c-1.014-3.64-3.398-5.49-7.088-5.506h-.009c-2.593.017-4.56.885-5.852 2.58-1.266 1.66-1.928 4.032-1.966 7.054v.468c.038 3.02.7 5.39 1.966 7.048 1.29 1.695 3.258 2.563 5.852 2.58h.009c2.282-.016 3.992-.618 5.232-1.843.958-.944 1.573-2.212 1.83-3.768l2.748.576c-.334 2.06-1.18 3.784-2.518 5.122-1.733 1.733-4.04 2.624-6.862 2.648zm2.99-10.893c-.085-1.834-.92-3.267-2.488-4.264-.905-.575-2.016-.877-3.307-.9h-.003c-.948.019-1.768.19-2.432.507-.852.41-1.47 1.03-1.784 1.793-.3.726-.369 1.516-.191 2.217.358 1.396 1.615 2.396 3.24 2.574.79.087 1.562.04 2.28-.143 1.105-.28 1.866-.888 2.201-1.757.187-.486.2-1.007.04-1.524-.221-.72-.739-1.227-1.538-1.505-.313-.11-.649-.169-.997-.175h-.012c-.48.008-.898.095-1.237.257a1.315 1.315 0 00-.642.785l2.566.537c-.086.387-.327.72-.691.96-.408.268-.94.405-1.578.405h-.083c-1.233-.033-2.188-.504-2.764-1.363-.37-.551-.537-1.228-.483-1.96.073-1 .524-1.835 1.267-2.35.64-.444 1.46-.684 2.373-.694h.038c.966.016 1.816.266 2.528.742 1.048.703 1.658 1.747 1.813 3.102.078.686.014 1.383-.187 2.08-.36 1.245-1.16 2.236-2.315 2.87-.94.516-2.04.783-3.268.796h-.088c-1.476-.02-2.727-.43-3.72-1.217-1.095-.868-1.822-2.152-2.115-3.752l2.74-.554c.181.978.567 1.72 1.146 2.203.53.443 1.225.676 2.065.695h.05c.747-.007 1.397-.165 1.932-.472.643-.37 1.062-.9 1.208-1.532.089-.382.076-.746-.037-1.066z"/>
        </svg>
      );
    case 'bluesky':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.785 2.627 3.6 3.501 6.158 3.186-3.726.565-7.016 1.964-2.816 6.755C7.834 24.234 9.78 18.206 12 18.206c2.22 0 4.166 6.028 8.034 1.982 4.2-4.791.91-6.19-2.816-6.755 2.558.315 5.373-.559 6.158-3.186.246-.828.624-5.789.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
        </svg>
      );
    default:
      return <Globe className={className} style={{ width: s, height: s }} />;
  }
};

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function startOfWeek(date: Date, mondayStart = true) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = mondayStart ? (day === 0 ? -6 : 1 - day) : -day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Get the display text from a post (API returns different fields depending on endpoint) */
function getPostText(post: PostItem): string {
  return post.caption || post.text || post.title || '';
}

/** Get the date string to group a post by */
function getPostDate(post: PostItem): string {
  return (post.start || post.scheduled_at || post.published_at || post.created_at || '').split('T')[0];
}

function formatEngagement(n: number | undefined | null): string {
  const v = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

function normalizePostStatus(status?: string): PostItem['status'] {
  return status === 'scheduled' || status === 'published' || status === 'failed' || status === 'draft'
    ? status
    : 'draft';
}

function feedPostToPostItem(post: FeedPost): PostItem {
  return {
    id: post.id,
    text: post.text,
    status: normalizePostStatus(post.status),
    platforms: post.target_platforms || [],
    accounts: post.target_accounts || [],
    media_urls: post.media_thumbnail ? [post.media_thumbnail] : [],
    media_count: post.media_count,
    scheduled_at: post.scheduled_at,
    published_at: post.published_at,
    created_at: post.created_at,
    total_likes: post.total_likes,
    total_comments: post.total_comments,
    is_favorite: post.is_favorite,
  };
}

/* ------------------------------------------------------------------ */
/*  Sub-component: Compact Post Bar (calendar cell item)               */
/* ------------------------------------------------------------------ */

const PostBar: React.FC<{
  post: PostItem;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onToDraft: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  theme: Theme;
}> = ({ post, isSelected, onSelect, onEdit, onDuplicate, onToDraft, onDelete, onToggleFavorite, theme }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Dedupe while preserving order — the API occasionally repeats a platform
  // once per connected account, which we don't want to re-render as two chips.
  const uniquePlatforms = Array.from(new Set(post.platforms || []));
  const mainPlatform = uniquePlatforms[0];
  const platformColor = PLATFORM_COLORS[mainPlatform] || theme.hex.primary;
  const timeStr = post.scheduled_at || post.published_at || post.created_at;

  // How many platform chips to render inline before collapsing the rest
  // into a `+N` overflow badge. The calendar cells are narrow so we cap
  // at three — wider views (day/week) still use the same component and
  // will show the overflow count when the post is cross-posted widely.
  const MAX_VISIBLE_PLATFORMS = 3;
  const visiblePlatforms = uniquePlatforms.slice(0, MAX_VISIBLE_PLATFORMS);
  const hiddenPlatformCount = uniquePlatforms.length - visiblePlatforms.length;

  // Human-readable list of all platforms — surfaced via `title` on the
  // icon group so the full set is discoverable on hover even when some
  // are hidden behind `+N`.
  const platformTitle = uniquePlatforms
    .map((pid) => PLATFORMS.find((p) => p.id === pid)?.name || pid)
    .join(', ');

  // Status-based left border color
  const borderColor =
    post.status === 'published' ? '#10b981' :
    post.status === 'scheduled' ? platformColor :
    post.status === 'failed' ? '#ef4444' :
    '#9ca3af';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      className={`group relative flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer transition-all text-left w-full border-l-[3px] ${
        isSelected ? 'bg-gray-100 shadow-sm' : 'hover:bg-gray-50'
      }`}
      style={{ borderLeftColor: borderColor }}
    >
      {/* Platform icon strip — one chip per targeted platform, with a
          `+N` overflow badge when the set is wider than the cell allows.
          Each chip carries the platform's brand color AND logo so the
          user can identify every destination at a glance. */}
      {visiblePlatforms.length > 0 && (
        <div className="flex -space-x-1 shrink-0" title={platformTitle}>
          {visiblePlatforms.map((pid) => {
            const c = PLATFORM_COLORS[pid] || '#999';
            const pinfo = PLATFORMS.find((p) => p.id === pid);
            return (
              <span
                key={pid}
                className="w-4 h-4 rounded-full flex items-center justify-center text-white ring-1 ring-white"
                style={{ backgroundColor: c }}
                aria-label={pinfo?.name || pid}
              >
                <PlatformIcon platform={pid} size={10} />
              </span>
            );
          })}
          {hiddenPlatformCount > 0 && (
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-gray-700 bg-gray-200 ring-1 ring-white"
              aria-label={`${hiddenPlatformCount} more platform${hiddenPlatformCount === 1 ? '' : 's'}`}
            >
              +{hiddenPlatformCount}
            </span>
          )}
        </div>
      )}

      {/* Time */}
      <span className="text-[10px] text-gray-500 whitespace-nowrap">{timeStr ? formatTime(timeStr) : '--:--'}</span>

      {/* Three-dot menu */}
      <div className="relative ml-auto shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity"
        >
          <MoreVertical className="h-3 w-3 text-gray-400" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" role="presentation" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }} />
            <div className="absolute right-0 top-5 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-32">
              <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onToggleFavorite(); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-1.5 font-medium">
                <Star className="h-3 w-3" /> {post.is_favorite ? t('ws_unfavorite', 'Unfavorite') : t('ws_favorite', 'Favorite')}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit(); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 font-medium">{t('ws_edit', 'Edit')}</button>
              <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDuplicate(); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 font-medium">{t('ws_duplicate', 'Duplicate')}</button>
              {(post.status === 'scheduled' || post.status === 'failed') && (
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onToDraft(); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 font-medium">{t('ws_to_drafts', 'To drafts')}</button>
              )}
              <div className="border-t border-gray-100 mt-1 pt-1">
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(); }} className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 font-medium">{t('ws_delete', 'Delete')}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Sub-component: Account Sidebar (Left)                              */
/* ------------------------------------------------------------------ */

const AccountSidebar: React.FC<{
  accounts: SocialAccount[];
  selectedAccount: string | null;
  onSelect: (id: string | null) => void;
  accountFilter: AccountFilter;
  onFilterChange: (f: AccountFilter) => void;
  theme: Theme;
}> = ({ accounts, selectedAccount, onSelect, onFilterChange, theme }) => {
  const { t } = useTranslation();
  const grouped = useMemo(() => {
    const map: Record<string, SocialAccount[]> = {};
    accounts.forEach((a) => {
      const key = a.platform;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [accounts]);

  return (
    <div className="w-52 shrink-0 border-r border-gray-100 bg-white flex flex-col overflow-hidden">
      {/* Add account button */}
      <div className="p-3">
        <button
          onClick={() => window.location.href = '/accounts'}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400 hover:border-gray-300 hover:text-gray-600 transition-colors font-medium"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {t('ws_add_account', 'Add account')}
        </button>
      </div>

      <div className="px-3 pb-2 border-b border-gray-200 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
          {t('ws_connected_accounts', 'Connected Accounts')}
        </span>
        {selectedAccount && (
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              onFilterChange('all');
            }}
            className="text-[10px] font-semibold hover:underline"
            style={{ color: theme.hex.primary }}
          >
            {t('ws_clear', 'Clear')}
          </button>
        )}
      </div>

      {/* Account list */}
      <div className="flex-1 overflow-y-auto">
        {Object.entries(grouped).map(([platformId, accts]) => (
          <div key={platformId}>
            {accts.map((a) => {
              const isActive = selectedAccount === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => onSelect(isActive ? null : a.id)}
                  className={`w-full px-3 py-2 text-left flex items-center gap-2.5 transition-all rounded-lg mx-2 my-0.5 w-[calc(100%-16px)] ${
                    isActive
                      ? 'bg-emerald-50 border border-emerald-200'
                      : 'border border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="relative shrink-0">
                    <AccountAvatar
                      src={a.avatar_url}
                      name={a.display_name}
                      username={a.username}
                      size={32}
                    />
                    {/* Platform badge */}
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white border-2 border-white"
                      style={{ backgroundColor: PLATFORM_COLORS[a.platform] || '#999' }}
                    >
                      <PlatformIcon platform={a.platform} size={8} />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold tracking-tight text-gray-900 truncate">{a.display_name || a.username}</p>
                    <p className="text-[10px] text-gray-400 truncate">@{a.username}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ))}

        {accounts.length === 0 && (
          <div className="p-4 text-center text-xs text-gray-400">{t('ws_no_accounts', 'No accounts connected')}</div>
        )}
      </div>

      {/* Bottom info */}
      <div className="border-t border-gray-200 px-3 py-2 text-[10px] text-gray-400">
        {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} | UTC{new Date().getTimezoneOffset() <= 0 ? '+' : '-'}{String(Math.abs(Math.floor(new Date().getTimezoneOffset() / 60))).padStart(2, '0')}:00
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Sub-component: Preview Panel (Right)                               */
/* ------------------------------------------------------------------ */

const CONTENT_TYPE_META: Record<string, { emoji: string; label: string }> = {
  post: { emoji: '📝', label: 'Post' },
  story: { emoji: '🎬', label: 'Story' },
  reel: { emoji: '🎞️', label: 'Reel' },
  carousel: { emoji: '🖼️', label: 'Carousel' },
  igtv: { emoji: '📺', label: 'IGTV' },
  short: { emoji: '⚡', label: 'Short' },
};

const STATUS_META: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  published: { label: 'Published', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  failed: { label: 'Failed', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

const PreviewPanel: React.FC<{
  post: PostItem | null;
  accounts: SocialAccount[];
  theme: Theme;
  activeTab: RightTab;
  onTabChange: (t: RightTab) => void;
  onToggleFavorite?: () => void;
  onClose?: () => void;
}> = ({ post, accounts, theme, activeTab, onTabChange, onToggleFavorite, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Adapt the workspace post shape to PostPreview's expected props. The
  // composer's preview works in terms of ``UrlMedia`` records, so we wrap
  // the post's media URLs into minimal "done" entries. No File objects are
  // available here (posts are persisted), so ``mediaFiles`` stays empty.
  // Hooks must run before any early return — compute from the (possibly
  // null) post and fall through.
  const previewUrlMedia: UrlMedia[] = useMemo(() => {
    if (!post) { return []; }
    return (post.media_urls || []).filter(Boolean).map((url, idx) => {
      const lower = url.toLowerCase();
      const isVideo = /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/.test(lower);
      return {
        id: `${post.id}-media-${idx}`,
        url,
        filename: url.split('/').pop() || `media-${idx}`,
        contentType: isVideo ? 'video/mp4' : 'image/jpeg',
        type: isVideo ? 'video' : 'image',
        status: 'done',
        progress: 100,
      };
    });
  }, [post]);

  // Only pass accounts that are actually targeted by this post's platform
  // list — PostPreview uses ``selectedAccounts`` to pick the right avatar
  // per tab.
  const previewSelectedAccounts = useMemo(() => {
    if (!post) { return []; }
    const platSet = new Set(post.platforms || []);
    return accounts
      .filter((a) => platSet.has(a.platform))
      .map((a) => a.id);
  }, [accounts, post]);

  if (!post) {
    return (
      <div className="w-80 shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
        <div className="flex-shrink-0 h-10 px-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs text-gray-400">{t('ws_preview', 'Preview')}</span>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <Eye className="h-7 w-7 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-400">{t('ws_select_post_preview', 'Select a post to preview')}</p>
          </div>
        </div>
      </div>
    );
  }

  const mainPlatform = post.platforms[0];
  const account = accounts.find((a) => a.platform === mainPlatform);
  const username = account?.username || 'username';
  const avatarUrl = account?.avatar_url;
  const displayCaption = getPostText(post) || 'No caption';

  // Placeholder kept so the rest of the panel renders while the real
  // platform preview lives inline via <PostPreview>.
  const renderMobilePreview = () => {
    return (
      <PostPreview
        selectedPlatforms={post.platforms || []}
        platforms={PLATFORMS}
        caption={displayCaption === 'No caption' ? '' : displayCaption}
        mediaFiles={[]}
        urlMediaItems={previewUrlMedia}
        linkPreview={null}
        connectedAccounts={accounts}
        selectedAccounts={previewSelectedAccounts}
      />
    );
  };

  const contentTypeMeta = CONTENT_TYPE_META[post.content_type || 'post'] || CONTENT_TYPE_META.post;
  const statusMeta = STATUS_META[post.status] || STATUS_META.draft;
  const platformNames = (post.platforms || [])
    .map((pl) => PLATFORMS.find((x) => x.id === pl)?.name || pl)
    .join(', ');
  const postTitle = post.title || (displayCaption ? displayCaption.slice(0, 60) : '(untitled)');
  const hashtagMatches = (displayCaption.match(/#[\w-]+/g) || []).slice(0, 8);

  return (
    <div className="w-80 shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
      {/* Panel header (Notion-style) */}
      <div className="flex-shrink-0 h-10 px-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => navigate(`/compose/${post.id}`)}
            title={t('ws_open_composer', 'Open in composer')}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-0.5">
          {onToggleFavorite && (
            <button
              onClick={onToggleFavorite}
              title={post.is_favorite ? t('ws_remove_favorite', 'Remove from favorites') : t('ws_add_favorite', 'Add to favorites')}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <Star className={`h-3.5 w-3.5 ${post.is_favorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              title={t('ws_close_preview', 'Close preview')}
              className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs: Preview / Comments / Activity — underline style */}
      <div className="flex-shrink-0 px-4 border-b border-gray-200 flex items-center gap-4 text-sm">
        {(['preview', 'comments', 'activity'] as RightTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={`py-2.5 capitalize transition-colors ${
              activeTab === tab
                ? 'text-gray-900 font-medium border-b-2 border-gray-900 -mb-px'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            {tab}
            {tab === 'comments' && (post.total_comments ?? 0) > 0 && (
              <span className="ml-1 text-xs text-gray-400">{post.total_comments}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'preview' && (
          <div>
            {/* Title block */}
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-start gap-2 mb-1">
                <span className="text-2xl leading-none mt-0.5 shrink-0" aria-hidden="true">{contentTypeMeta.emoji}</span>
                <h2 className="text-xl font-bold text-gray-900 leading-tight break-words">{postTitle}</h2>
              </div>
            </div>

            {/* Properties table (Notion database row style) */}
            <div className="px-5 pb-4 space-y-1 text-sm">
              <div className="flex items-start gap-3 py-1">
                <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Status</span>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${statusMeta.bg} ${statusMeta.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                  {statusMeta.label}
                </span>
              </div>
              {post.scheduled_at && (
                <div className="flex items-start gap-3 py-1">
                  <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Scheduled</span>
                  <span className="text-gray-700 text-sm">{new Date(post.scheduled_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              )}
              {post.published_at && (
                <div className="flex items-start gap-3 py-1">
                  <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Published</span>
                  <span className="text-gray-700 text-sm">{new Date(post.published_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              )}
              <div className="flex items-start gap-3 py-1">
                <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Platforms</span>
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  {(post.platforms || []).length > 0 ? (
                    <>
                      <div className="flex -space-x-1.5 shrink-0">
                        {(post.platforms || []).slice(0, 4).map((pl) => (
                          <div
                            key={pl}
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white ring-2 ring-white"
                            style={{ backgroundColor: PLATFORM_COLORS[pl] || '#9ca3af' }}
                            title={PLATFORMS.find((x) => x.id === pl)?.name || pl}
                          >
                            <PlatformIcon platform={pl} size={10} />
                          </div>
                        ))}
                      </div>
                      <span className="text-gray-600 text-xs truncate">{platformNames}</span>
                    </>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3 py-1">
                <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Account</span>
                <div className="flex items-center gap-1.5">
                  <AccountAvatar src={avatarUrl} name={account?.display_name} username={username} size={18} />
                  <span className="text-gray-700 text-sm truncate">{account?.display_name || username}</span>
                </div>
              </div>
              <div className="flex items-start gap-3 py-1">
                <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Content type</span>
                <span className="inline-flex items-center gap-1 text-gray-700 text-sm">
                  <span>{contentTypeMeta.emoji}</span>
                  <span>{contentTypeMeta.label}</span>
                </span>
              </div>
              <div className="flex items-start gap-3 py-1">
                <span className="w-24 text-gray-500 text-xs pt-0.5 shrink-0">Created</span>
                <span className="text-gray-700 text-sm">{new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Caption block */}
            {displayCaption && displayCaption !== 'No caption' && (
              <>
                <div className="px-5 py-4">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-2">Caption</div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{displayCaption}</p>
                  {hashtagMatches.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {hashtagMatches.map((tag, i) => (
                        <span key={i} className="text-xs text-sky-600">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-100" />
              </>
            )}

            {/* Platform preview section */}
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-3">Platform preview</div>
              {renderMobilePreview()}
            </div>
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="p-3 space-y-3">
            {post.status === 'published' ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">
                    {t('ws_comments', 'Comments')} <span className="text-gray-400 font-normal">({post.total_comments || 0})</span>
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <MessageCircle className="h-6 w-6 text-gray-300 mx-auto mb-1.5" />
                  <p className="text-[11px] text-gray-500">
                    {t('ws_comments_in_inbox', 'Manage replies from the unified inbox.')}
                  </p>
                  <p className="text-[9px] text-gray-300 mt-0.5">
                    {t('ws_comments_sync_hint', 'Published post comments sync there when the platform connection supports it.')}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/inbox')}
                    className="mt-3 px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white"
                    style={{ backgroundColor: theme.hex.primary }}
                  >
                    {t('ws_open_inbox', 'Open Inbox')}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <MessageCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400">{t('ws_comments_after_publish', 'Comments are available after publishing')}</p>
                <p className="text-[10px] text-gray-300 mt-1">
                  {t('ws_post_status_label', 'Post status')}: <span className="capitalize font-semibold">{post.status}</span>
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="p-3 space-y-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-700">Activity Log</span>
            </div>
            {/* Activity timeline */}
            <div className="space-y-0">
              {/* Created event */}
              <div className="flex gap-2.5 py-2 border-b border-gray-50">
                <div className="shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <Plus className="h-2.5 w-2.5 text-blue-600" />
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-gray-700">Post created</p>
                  <p className="text-[9px] text-gray-400">{new Date(post.created_at).toLocaleString()}</p>
                </div>
              </div>

              {/* Scheduled event */}
              {post.scheduled_at && (
                <div className="flex gap-2.5 py-2 border-b border-gray-50">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="h-2.5 w-2.5 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-700">Scheduled for publishing</p>
                    <p className="text-[9px] text-gray-400">{new Date(post.scheduled_at).toLocaleString()}</p>
                  </div>
                </div>
              )}

              {/* Published event */}
              {post.published_at && (
                <div className="flex gap-2.5 py-2 border-b border-gray-50">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-2.5 w-2.5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-700">Published to {post.platforms.map(pl => PLATFORMS.find(x => x.id === pl)?.name).filter(Boolean).join(', ')}</p>
                    <p className="text-[9px] text-gray-400">{new Date(post.published_at).toLocaleString()}</p>
                  </div>
                </div>
              )}

              {/* Failed event */}
              {post.status === 'failed' && (
                <div className="flex gap-2.5 py-2 border-b border-gray-50">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertCircle className="h-2.5 w-2.5 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] text-red-600">Publishing failed</p>
                    <p className="text-[9px] text-gray-400">Check post settings and retry</p>
                  </div>
                </div>
              )}

              {/* Platforms info */}
              <div className="flex gap-2.5 py-2">
                <div className="shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                    <Globe className="h-2.5 w-2.5 text-gray-500" />
                  </div>
                </div>
                <div>
                  <p className="text-[11px] text-gray-700">Platforms: {post.platforms.map(pl => PLATFORMS.find(x => x.id === pl)?.icon).join(' ')}</p>
                  <p className="text-[9px] text-gray-400">{post.platforms.length} platform{post.platforms.length !== 1 ? 's' : ''} selected</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex-shrink-0 px-3 py-2.5 border-t border-gray-200 flex items-center gap-2">
        <button
          onClick={() => navigate(`/compose/${post.id}`)}
          className="flex-1 px-3 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
        >
          Open
        </button>
        <button
          onClick={() => navigate(`/compose?duplicate=${post.id}`)}
          className="px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
        >
          <Copy className="h-3.5 w-3.5" />
          Duplicate
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main: WorkspacePage                                                */
/* ------------------------------------------------------------------ */

export default function WorkspacePage() {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contentFilter, setContentFilter] = useState<ContentFilter>('posts');
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [centerTab, setCenterTab] = useState<CenterTab>('posting');
  const [rightTab, setRightTab] = useState<RightTab>('preview');
  const [accountFilter, setAccountFilter] = useState<AccountFilter>('all');
  const [postingViewMode, setPostingViewMode] = useState<'calendar' | 'grid' | 'list'>('calendar');

  // Compute date range based on view
  const { startDate, endDate } = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const d = currentDate.getDate();

    if (calendarView === 'day') {
      const start = new Date(y, m, d);
      const end = new Date(y, m, d + 1);
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }
    if (calendarView === 'week') {
      const ws = startOfWeek(currentDate);
      const we = new Date(ws);
      we.setDate(we.getDate() + 7);
      return { startDate: ws.toISOString().split('T')[0], endDate: we.toISOString().split('T')[0] };
    }
    // month
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    const calStart = startOfWeek(firstDay);
    const calEnd = new Date(lastDay);
    const endDayOffset = calEnd.getDay() === 0 ? 0 : 7 - calEnd.getDay();
    calEnd.setDate(calEnd.getDate() + endDayOffset + 1);
    return { startDate: calStart.toISOString().split('T')[0], endDate: calEnd.toISOString().split('T')[0] };
  }, [calendarView, currentDate]);

  // Fetch data
  const { data: calendarData, isLoading: postsLoading } = useQuery({
    queryKey: ['workspace-calendar', startDate, endDate],
    queryFn: () => postsAPI.calendar(startDate, endDate).then((r) => r.data),
    staleTime: 30_000,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => socialAPI.list().then((r) => r.data),
    staleTime: 60_000,
  });

  const accounts: SocialAccount[] = useMemo(
    () => {
      if (!accountsData) return [];
      if (Array.isArray(accountsData)) return accountsData;
      if (Array.isArray((accountsData as Record<string, unknown>).accounts)) return (accountsData as Record<string, unknown>).accounts as SocialAccount[];
      if (Array.isArray((accountsData as Record<string, unknown>).data)) return (accountsData as Record<string, unknown>).data as SocialAccount[];
      return [];
    },
    [accountsData],
  );

  // Analytics data queries (fetched when analytics/favorites tabs are active)
  const { data: analyticsOverviewData } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: () => analyticsAPI.overview('30d').then((r) => r.data),
    staleTime: 120_000,
    enabled: centerTab === 'analytics' || centerTab === 'favorites',
  });

  const { data: platformDistData } = useQuery({
    queryKey: ['analytics-platform-dist'],
    queryFn: () => analyticsAPI.platformDistribution('30d').then((r) => r.data),
    staleTime: 120_000,
    enabled: centerTab === 'analytics',
  });

  const { data: contentPerfData } = useQuery({
    queryKey: ['analytics-content-perf'],
    queryFn: () => analyticsAPI.contentPerformance('30d').then((r) => r.data),
    staleTime: 120_000,
    enabled: centerTab === 'analytics',
  });

  const { data: topPostsData } = useQuery({
    queryKey: ['analytics-top-posts'],
    queryFn: () => analyticsAPI.posts({ period: '30d', sort_by: 'engagement' }).then((r) => r.data),
    staleTime: 120_000,
    enabled: centerTab === 'analytics' || centerTab === 'favorites',
  });

  const { data: bestTimesData } = useQuery({
    queryKey: ['analytics-best-times'],
    queryFn: () => analyticsAPI.bestTimes().then((r) => r.data),
    staleTime: 300_000,
    enabled: centerTab === 'analytics',
  });

  // Dedicated feed query — fetches ALL published posts, not just current calendar range
  const [feedPage, setFeedPage] = useState(1);
  const { data: feedData, isLoading: feedLoading } = useQuery({
    queryKey: ['workspace-feed', feedPage],
    queryFn: () => postsAPI.list({ page: feedPage }).then((r) => r.data),
    staleTime: 30_000,
    enabled: centerTab === 'feed',
  });

  // Favorites query — fetches posts marked as favorite
  const { data: favoritesData, isLoading: favoritesLoading } = useQuery({
    queryKey: ['workspace-favorites'],
    queryFn: () => postsAPI.list({ favorites: true, page: 1 }).then((r) => r.data),
    staleTime: 30_000,
    enabled: centerTab === 'favorites',
  });
  const favoritePosts: FeedPost[] = useMemo(() => favoritesData?.posts || [], [favoritesData?.posts]);

  const analyticsOverview: AnalyticsOverview | null = analyticsOverviewData || null;
  const platformDist: PlatformDistItem[] = Array.isArray(platformDistData?.platforms) ? platformDistData.platforms : Array.isArray(platformDistData) ? platformDistData : [];
  const contentPerf: ContentPerfItem[] = Array.isArray(contentPerfData?.content_types) ? contentPerfData.content_types : Array.isArray(contentPerfData) ? contentPerfData : [];
  const topPosts: AnalyticsPost[] = Array.isArray(topPostsData?.posts) ? topPostsData.posts : Array.isArray(topPostsData) ? topPostsData : [];

  // Extract & filter posts
  const allPosts: PostItem[] = useMemo(() => {
    if (!calendarData) return [];
    const events = calendarData.events || calendarData || [];
    return events as PostItem[];
  }, [calendarData]);

  const filteredPosts = useMemo(() => {
    let posts = allPosts;
    if (selectedAccount) {
      posts = posts.filter((p) => p.accounts?.includes(selectedAccount) || p.platforms.some((plat) => {
        const acct = accounts.find((a) => a.id === selectedAccount);
        return acct && acct.platform === plat;
      }));
    }
    if (contentFilter !== 'all') {
      posts = posts.filter((p) => {
        const ct = p.content_type?.toLowerCase();
        if (contentFilter === 'posts') return !ct || ct === 'post' || ct === 'text';
        if (contentFilter === 'stories') return ct === 'story';
        if (contentFilter === 'videos') return ct === 'video' || ct === 'igtv';
        if (contentFilter === 'reels') return ct === 'reel';
        return true;
      });
    }
    return posts;
  }, [allPosts, selectedAccount, contentFilter, accounts]);

  // Group posts by date
  const postsByDate = useMemo(() => {
    const map: Record<string, PostItem[]> = {};
    filteredPosts.forEach((p) => {
      const dateStr = getPostDate(p);
      if (!dateStr) return;
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(p);
    });
    return map;
  }, [filteredPosts]);

  // Mutations
  const duplicateMutation = useMutation({
    mutationFn: (id: string) => postsAPI.duplicate(id),
    onSuccess: () => { toast.success('Post duplicated'); queryClient.invalidateQueries({ queryKey: ['workspace-calendar'] }); },
    onError: () => toast.error('Failed to duplicate'),
  });

  const toDraftMutation = useMutation({
    mutationFn: (id: string) => postsAPI.toDraft(id),
    onSuccess: () => { toast.success('Moved to drafts'); queryClient.invalidateQueries({ queryKey: ['workspace-calendar'] }); },
    onError: () => toast.error('Failed to move to drafts'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => postsAPI.delete(id),
    onSuccess: () => { toast.success('Post deleted'); setSelectedPostId(null); queryClient.invalidateQueries({ queryKey: ['workspace-calendar'] }); },
    onError: () => toast.error('Failed to delete'),
  });

  const favoriteMutation = useMutation({
    mutationFn: (id: string) => postsAPI.toggleFavorite(id),
    onSuccess: (res) => {
      const isFav = res.data?.is_favorite;
      toast.success(isFav ? 'Added to favorites' : 'Removed from favorites');
      queryClient.invalidateQueries({ queryKey: ['workspace-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-feed'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-favorites'] });
    },
    onError: () => toast.error('Failed to update favorite'),
  });

  // Navigation
  const navigatePrev = () => {
    const d = new Date(currentDate);
    if (calendarView === 'day') d.setDate(d.getDate() - 1);
    else if (calendarView === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };

  const navigateNext = () => {
    const d = new Date(currentDate);
    if (calendarView === 'day') d.setDate(d.getDate() + 1);
    else if (calendarView === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };

  // Post card action handlers
  const postActions = (postId: string) => ({
    onSelect: () => setSelectedPostId(postId),
    onEdit: () => navigate(`/compose/${postId}`),
    onDuplicate: () => duplicateMutation.mutate(postId),
    onToDraft: () => toDraftMutation.mutate(postId),
    onDelete: () => deleteMutation.mutate(postId),
    onToggleFavorite: () => favoriteMutation.mutate(postId),
  });

  // Connected account avatars for toolbar
  const accountAvatars = accounts.slice(0, 5);

  // Calendar title
  const calTitle = calendarView === 'month'
    ? `${currentDate.toLocaleDateString('en-US', { month: 'long' })} ${currentDate.getFullYear()}`
    : calendarView === 'week'
      ? (() => { const ws = startOfWeek(currentDate); const we = new Date(ws); we.setDate(we.getDate() + 6); return `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`; })()
      : currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // ---- Render month grid ----
  const renderMonthGrid = () => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const firstDay = new Date(y, m, 1);
    const daysInMonth = getDaysInMonth(y, m);
    const startDayMon = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday-start
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const cells: React.ReactNode[] = [];

    // Leading cells from previous month
    for (let i = 0; i < startDayMon; i++) {
      const prevDate = new Date(y, m, -(startDayMon - i - 1));
      const dateStr = prevDate.toISOString().split('T')[0];
      const dayPosts = postsByDate[dateStr] || [];
      cells.push(
        <div key={`prev-${i}`} className="border-b border-r border-gray-100 p-1.5 min-h-[96px] bg-gray-50/40">
          <span className="text-[11px] text-gray-300 font-medium">{prevDate.getDate()}</span>
          <div className="space-y-0.5 mt-1">
            {dayPosts.slice(0, 4).map((p) => (
              <PostBar key={p.id} post={p} isSelected={selectedPostId === p.id} theme={theme} {...postActions(p.id)} />
            ))}
          </div>
        </div>,
      );
    }

    // Month days
    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate = new Date(y, m, day);
      const dateStr = cellDate.toISOString().split('T')[0];
      const dayPosts = postsByDate[dateStr] || [];
      const isToday = isSameDay(cellDate, today);

      cells.push(
        <div key={day} className="group border-b border-r border-gray-100 p-1.5 min-h-[96px] hover:bg-gray-50/60 transition-colors">
          <div className="flex items-center justify-between">
            {isToday ? (
              <span
                className="text-[10px] font-semibold text-white rounded-full w-5 h-5 flex items-center justify-center"
                style={{ backgroundColor: theme.hex.primary }}
              >
                {day}
              </span>
            ) : (
              <span className="text-[11px] font-medium text-gray-700">{day}</span>
            )}
            <button
              onClick={() => navigate('/compose')}
              className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-gray-600 transition-opacity"
              aria-label="Add post"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
          <div className="space-y-0.5 mt-1">
            {dayPosts.slice(0, 4).map((p) => (
              <PostBar key={p.id} post={p} isSelected={selectedPostId === p.id} theme={theme} {...postActions(p.id)} />
            ))}
            {dayPosts.length > 4 && (
              <p className="text-[9px] text-gray-400 pl-1 pt-0.5">+{dayPosts.length - 4} more</p>
            )}
          </div>
        </div>,
      );
    }

    // Trailing cells
    const totalCells = cells.length;
    const remaining = 7 - (totalCells % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        cells.push(
          <div key={`next-${i}`} className="border-b border-r border-gray-100 p-1.5 min-h-[96px] bg-gray-50/40">
            <span className="text-[11px] text-gray-300 font-medium">{i}</span>
          </div>,
        );
      }
    }

    return (
      <div className="border-l border-t border-gray-100">
        <div className="grid grid-cols-7 border-b border-r border-gray-100 bg-gray-50/60">
          {WEEK_DAYS.map((d) => (
            <div key={d} className="text-left text-[10px] font-medium tracking-wide text-gray-500 py-2 px-2 uppercase border-r border-gray-100 last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">{cells}</div>
      </div>
    );
  };

  // ---- Render week view ----
  const renderWeekView = () => {
    const ws = startOfWeek(currentDate);
    const today = new Date(); today.setHours(0, 0, 0, 0);

    return (
      <div className="border-l border-t border-gray-100">
        <div className="grid grid-cols-7">
          {Array.from({ length: 7 }).map((_, i) => {
            const day = new Date(ws);
            day.setDate(day.getDate() + i);
            const isToday = isSameDay(day, today);
            return (
              <div key={`h-${i}`} className="border-b border-r border-gray-100 bg-gray-50/60 px-2 py-2 flex items-center gap-2">
                <span className="text-[10px] font-medium tracking-wide text-gray-500 uppercase">{WEEK_DAYS[i]}</span>
                {isToday ? (
                  <span
                    className="text-[10px] font-semibold text-white rounded-full w-5 h-5 flex items-center justify-center"
                    style={{ backgroundColor: theme.hex.primary }}
                  >
                    {day.getDate()}
                  </span>
                ) : (
                  <span className="text-[11px] font-medium text-gray-700">{day.getDate()}</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7">
          {Array.from({ length: 7 }).map((_, i) => {
            const day = new Date(ws);
            day.setDate(day.getDate() + i);
            const dateStr = day.toISOString().split('T')[0];
            const dayPosts = postsByDate[dateStr] || [];
            return (
              <div key={i} className="min-h-[420px] p-2 border-b border-r border-gray-100 hover:bg-gray-50/40 transition-colors">
                <div className="space-y-1">
                  {dayPosts.map((p) => (
                    <PostBar key={p.id} post={p} isSelected={selectedPostId === p.id} theme={theme} {...postActions(p.id)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- Render day view ----
  const renderDayView = () => {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dayPosts = postsByDate[dateStr] || [];

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isToday = isSameDay(currentDate, today);
    const dayOfWeek = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1;

    return (
      <div className="max-w-2xl mx-auto px-6 py-4">
        <div className="flex items-baseline gap-3 pb-3 border-b border-gray-100 mb-4">
          <span className="text-[10px] font-medium tracking-wide text-gray-500 uppercase">{WEEK_DAYS[dayOfWeek]}</span>
          {isToday ? (
            <span
              className="text-xs font-semibold text-white rounded-full w-6 h-6 flex items-center justify-center"
              style={{ backgroundColor: theme.hex.primary }}
            >
              {currentDate.getDate()}
            </span>
          ) : (
            <span className="text-lg font-semibold text-gray-900">{currentDate.getDate()}</span>
          )}
          <span className="text-xs text-gray-400 ml-auto">
            {dayPosts.length} {dayPosts.length === 1 ? 'post' : 'posts'}
          </span>
        </div>
        {dayPosts.length === 0 ? (
          <div className="text-center py-16">
            <CalendarIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-xs text-gray-400">{t('ws_no_posts_day', 'No posts scheduled for this day')}</p>
            <button
              onClick={() => navigate('/compose')}
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Plus className="h-3 w-3" />
              {t('ws_add_post', 'Add post')}
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {dayPosts.map((p) => (
              <PostBar key={p.id} post={p} isSelected={selectedPostId === p.id} theme={theme} {...postActions(p.id)} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- Feed view (grid of published posts with engagement) ----
  const [feedViewMode, setFeedViewMode] = useState<'grid' | 'list'>('list');
  const [feedPlatformFilter, setFeedPlatformFilter] = useState<string | null>(null);
  const [brokenImgs, setBrokenImgs] = useState<Set<string>>(new Set());

  const feedPostsRaw: FeedPost[] = useMemo(() => feedData?.posts || [], [feedData?.posts]);
  const feedTotal: number = feedData?.total || 0;

  const feedPosts = useMemo(() => {
    if (feedPlatformFilter) {
      return feedPostsRaw.filter((p) => p.target_platforms?.includes(feedPlatformFilter));
    }
    return feedPostsRaw;
  }, [feedPostsRaw, feedPlatformFilter]);

  const selectedPost = useMemo(() => {
    if (!selectedPostId) { return null; }
    const calendarPost = filteredPosts.find((p) => p.id === selectedPostId);
    if (calendarPost) { return calendarPost; }
    const feedPost = feedPostsRaw.find((p) => p.id === selectedPostId);
    if (feedPost) { return feedPostToPostItem(feedPost); }
    const favoritePost = favoritePosts.find((p) => p.id === selectedPostId);
    return favoritePost ? feedPostToPostItem(favoritePost) : null;
  }, [favoritePosts, feedPostsRaw, filteredPosts, selectedPostId]);

  const renderFeedContent = () => {
    if (feedPosts.length === 0) {
      return (
        <div className="text-center py-16 text-gray-400">
          <Grid3X3 className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No posts yet</p>
          <p className="text-xs text-gray-300 mt-1">Create your first post to see it here</p>
        </div>
      );
    }

    if (feedViewMode === 'grid') {
      return (
        <div className="grid grid-cols-3 gap-2 p-3">
          {feedPosts.map((p) => {
            const firstPlat = (p.target_platforms || [])[0];
            const platColor = firstPlat ? (PLATFORM_COLORS[firstPlat] || '#6b7280') : '#6b7280';
            const platObj = firstPlat ? PLATFORMS.find(x => x.id === firstPlat) : null;
            const postDate = p.published_at || p.scheduled_at || p.created_at;
            return (
              <button
                key={p.id}
                onClick={() => { setSelectedPostId(p.id); setRightTab('preview'); }}
                className={`relative group rounded-xl overflow-hidden text-left transition-all ${selectedPostId === p.id ? 'ring-2 ring-blue-500' : 'border border-gray-200'}`}
                style={selectedPostId === p.id ? { boxShadow: `0 0 0 2px ${theme.hex.primary}` } : undefined}
              >
                {/* Media or colored header */}
                {p.media_thumbnail && !brokenImgs.has(p.media_thumbnail) ? (
                  <div className="aspect-video relative">
                    <img src={p.media_thumbnail} alt="" className="w-full h-full object-cover" onError={() => { setBrokenImgs(prev => new Set(prev).add(p.media_thumbnail!)); }} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                      <span className="flex items-center gap-1 text-white text-[10px] font-semibold"><Heart className="h-3 w-3" /> {formatEngagement(p.total_likes)}</span>
                      <span className="flex items-center gap-1 text-white text-[10px] font-semibold"><MessageCircle className="h-3 w-3" /> {formatEngagement(p.total_comments)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="h-8 w-full" style={{ backgroundColor: `${platColor}15` }} />
                )}
                {/* Card body */}
                <div className="p-2.5">
                  <p className="text-[11px] text-gray-800 line-clamp-2 leading-snug min-h-[2rem]">{p.text || 'No caption'}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1.5">
                      {platObj && (
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: platColor }}>
                          <PlatformIcon platform={firstPlat || ''} size={10} />
                        </span>
                      )}
                      <span className="text-[9px] text-gray-400">
                        {postDate ? new Date(postDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${
                      p.status === 'published' ? 'bg-green-50 text-green-600' :
                      p.status === 'scheduled' ? 'bg-blue-50 text-blue-600' :
                      p.status === 'failed' ? 'bg-red-50 text-red-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {p.status}
                    </span>
                  </div>
                  {/* Engagement for text-only posts */}
                  {!p.media_thumbnail && (p.total_likes > 0 || p.total_comments > 0) && (
                    <div className="flex items-center gap-2 mt-1.5 text-[9px] text-gray-400">
                      <span className="flex items-center gap-0.5"><Heart className="h-2.5 w-2.5" /> {formatEngagement(p.total_likes)}</span>
                      <span className="flex items-center gap-0.5"><MessageCircle className="h-2.5 w-2.5" /> {formatEngagement(p.total_comments)}</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    // List view
    return (
      <div className="divide-y divide-gray-100">
        {feedPosts.map((p) => {
          const firstPlatform = (p.target_platforms || [])[0];
          const platColor = firstPlatform ? (PLATFORM_COLORS[firstPlatform] || '#6b7280') : '#6b7280';
          const postDate = p.published_at || p.scheduled_at || p.created_at;
          return (
            <button
              key={p.id}
              onClick={() => { setSelectedPostId(p.id); setRightTab('preview'); }}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${selectedPostId === p.id ? 'bg-gray-50' : ''}`}
            >
              {/* Thumbnail or platform-colored icon */}
              {p.media_thumbnail && !brokenImgs.has(p.media_thumbnail) ? (
                <img src={p.media_thumbnail} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" onError={() => { setBrokenImgs(prev => new Set(prev).add(p.media_thumbnail!)); }} />
              ) : (
                <div className="h-12 w-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${platColor}15`, color: platColor }}>
                  {firstPlatform ? (
                    <PlatformIcon platform={firstPlatform} size={22} />
                  ) : (
                    <FileEdit className="h-5 w-5 text-gray-300" />
                  )}
                </div>
              )}
              {/* Caption + meta */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-800 line-clamp-1">{p.text || 'No caption'}</p>
                <div className="flex items-center gap-2 mt-1">
                  {firstPlatform && (
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: platColor }}>
                      <PlatformIcon platform={firstPlatform} size={10} />
                    </span>
                  )}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                    p.status === 'published' ? 'bg-green-50 text-green-600' :
                    p.status === 'scheduled' ? 'bg-blue-50 text-blue-600' :
                    p.status === 'failed' ? 'bg-red-50 text-red-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {p.status}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {postDate ? new Date(postDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                  </span>
                </div>
              </div>
              {/* Engagement */}
              <div className="flex items-center gap-3 text-[10px] text-gray-400 shrink-0">
                <span className="flex items-center gap-0.5"><Heart className="h-3 w-3" /> {formatEngagement(p.total_likes)}</span>
                <span className="flex items-center gap-0.5"><MessageCircle className="h-3 w-3" /> {formatEngagement(p.total_comments)}</span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const contentFilters: { id: ContentFilter; label: string }[] = [
    { id: 'posts', label: t('ws_posts', 'Posts') },
    { id: 'stories', label: t('ws_stories', 'Stories') },
    { id: 'videos', label: t('ws_igtv', 'IGTV') },
    { id: 'reels', label: t('ws_reels', 'Reels') },
  ];

  const centerTabs: { id: CenterTab; label: string; icon: React.ElementType }[] = [
    { id: 'posting', label: t('ws_posting', 'Posting'), icon: CalendarIcon },
    { id: 'feed', label: t('ws_feed', 'Feed'), icon: Grid3X3 },
    { id: 'favorites', label: t('ws_favorites', 'Favorites'), icon: Star },
    { id: 'analytics', label: t('ws_analytics', 'Analytics'), icon: BarChart3 },
    { id: 'settings', label: t('ws_settings', 'Settings'), icon: Settings },
  ];

  return (
    <div className="h-[calc(100vh-64px)] flex overflow-hidden bg-white">
      {/* Left: Account Sidebar */}
      <AccountSidebar
        accounts={accounts}
        selectedAccount={selectedAccount}
        onSelect={setSelectedAccount}
        accountFilter={accountFilter}
        onFilterChange={setAccountFilter}
        theme={theme}
      />

      {/* Center: Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top sub-tabs: Posting / Feed / Favorites / Analytics / Settings */}
        <div className="shrink-0 border-b border-gray-100 px-4 flex items-center gap-1 bg-white py-2">
          {centerTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = centerTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setCenterTab(tab.id)}
                className={`px-3.5 py-1.5 text-xs font-semibold flex items-center gap-1.5 rounded-lg transition-colors ${
                  isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Posting tab content */}
        {centerTab === 'posting' && (
          <>
            {/* Toolbar: Add post + content filters + view controls */}
            <div className="shrink-0 border-b border-gray-100 px-4 py-2 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2">
                {/* Add post button — outlined so it doesn't look "selected" */}
                <button
                  onClick={() => navigate('/compose')}
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 py-2 rounded-xl text-xs font-bold border transition-all hover:text-white active:scale-95"
                  style={{ color: theme.hex.primary, borderColor: theme.hex.primary }}
                  onMouseEnter={e => { (e.currentTarget.style.backgroundColor = theme.hex.primary); (e.currentTarget.style.color = '#fff'); }}
                  onMouseLeave={e => { (e.currentTarget.style.backgroundColor = 'transparent'); (e.currentTarget.style.color = theme.hex.primary); }}
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" />
                  {t('ws_add_post', 'Add post')}
                </button>

                {/* Content type pills */}
                {contentFilters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setContentFilter(contentFilter === f.id ? 'all' : f.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                      contentFilter === f.id
                        ? 'text-white border-transparent shadow-sm'
                        : 'text-gray-500 border-gray-200 bg-white hover:border-gray-300 hover:text-gray-700'
                    }`}
                    style={contentFilter === f.id ? { backgroundColor: theme.hex.primary } : undefined}
                  >
                    {f.id === 'posts' && <Send className="h-3 w-3" />}
                    {f.id === 'stories' && <ImageIcon className="h-3 w-3" />}
                    {f.id === 'videos' && <Video className="h-3 w-3" />}
                    {f.id === 'reels' && <Video className="h-3 w-3" />}
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                {/* View mode icons: grid / list / calendar */}
                <div className="flex bg-gray-100 rounded-xl p-1 gap-0.5">
                  <button onClick={() => setPostingViewMode('grid')} className={`p-1.5 rounded-lg transition-colors ${postingViewMode === 'grid' ? 'bg-white shadow-sm text-gray-700' : 'hover:bg-white/60 text-gray-400'}`}><Grid3X3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setPostingViewMode('list')} className={`p-1.5 rounded-lg transition-colors ${postingViewMode === 'list' ? 'bg-white shadow-sm text-gray-700' : 'hover:bg-white/60 text-gray-400'}`}><List className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setPostingViewMode('calendar')} className={`p-1.5 rounded-lg transition-colors ${postingViewMode === 'calendar' ? 'bg-white shadow-sm text-gray-700' : 'hover:bg-white/60 text-gray-400'}`}><CalendarIcon className="h-3.5 w-3.5" /></button>
                </div>

                {/* Day / Week / Month toggle (calendar view only) */}
                {postingViewMode === 'calendar' && (
                  <div className="flex items-center gap-0 text-xs">
                    {(['day', 'week', 'month'] as CalendarView[]).map((v) => (
                      <button
                        key={v}
                        onClick={() => setCalendarView(v)}
                        className={`px-2.5 py-1 capitalize transition-colors ${
                          calendarView === v ? 'font-semibold' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        style={calendarView === v ? { color: theme.hex.primary } : undefined}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                )}

                {/* Navigation + month label (calendar view only) */}
                {postingViewMode === 'calendar' && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={navigatePrev} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="h-4 w-4 text-gray-500" /></button>
                    <span className="text-xs font-bold tracking-tight text-gray-700 min-w-[100px] text-center">{calTitle}</span>
                    <button onClick={navigateNext} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="h-4 w-4 text-gray-500" /></button>
                  </div>
                )}

                {/* Account avatars */}
                <div className="flex -space-x-1.5">
                  {accountAvatars.map((a) => (
                    <AccountAvatar
                      key={a.id}
                      src={a.avatar_url}
                      name={a.display_name}
                      username={a.username}
                      size={24}
                      title={a.username}
                      className="border-2 border-white"
                    />
                  ))}
                  {accounts.length > 5 && (
                    <span className="h-6 w-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[8px] text-gray-500">
                      +{accounts.length - 5}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-auto">
              {postsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-300 rounded-full" style={{ borderTopColor: theme.hex.primary }} />
                </div>
              ) : postingViewMode === 'calendar' ? (
                <>
                  {calendarView === 'month' && renderMonthGrid()}
                  {calendarView === 'week' && renderWeekView()}
                  {calendarView === 'day' && renderDayView()}
                </>
              ) : postingViewMode === 'grid' ? (
                filteredPosts.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <Grid3X3 className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">{t('ws_no_posts', 'No posts found')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 p-3">
                    {filteredPosts.map((p) => {
                      const platIcons = (p.platforms || []).slice(0, 3);
                      const postDate = p.scheduled_at || p.published_at || p.created_at;
                      const statusColors: Record<string, string> = {
                        published: 'bg-green-50 text-green-600',
                        scheduled: 'bg-blue-50 text-blue-600',
                        failed: 'bg-red-50 text-red-600',
                        draft: 'bg-gray-100 text-gray-500',
                      };
                      return (
                        <button
                          key={p.id}
                          onClick={() => { setSelectedPostId(p.id); setRightTab('preview'); }}
                          className={`relative group rounded-xl overflow-hidden text-left transition-all ${selectedPostId === p.id ? 'ring-2' : 'border border-gray-200'}`}
                          style={selectedPostId === p.id ? { boxShadow: `0 0 0 2px ${theme.hex.primary}` } : undefined}
                        >
                          {(p.media_urls && p.media_urls.length > 0) ? (
                            <div className="aspect-video relative">
                              <img src={p.media_urls[0]} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                                {p.total_likes !== undefined && <span className="flex items-center gap-1 text-white text-[10px] font-semibold"><Heart className="h-3 w-3" /> {p.total_likes}</span>}
                                {p.total_comments !== undefined && <span className="flex items-center gap-1 text-white text-[10px] font-semibold"><MessageCircle className="h-3 w-3" /> {p.total_comments}</span>}
                              </div>
                            </div>
                          ) : (
                            <div className="h-8 w-full bg-gray-50" />
                          )}
                          <div className="p-2.5">
                            <p className="text-[11px] text-gray-800 line-clamp-2 leading-snug min-h-[2rem]">{p.caption || p.text || p.title || 'No caption'}</p>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-1">
                                {platIcons.map((pl) => (
                                  <span key={pl} className="w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: PLATFORM_COLORS[pl] || '#6b7280' }}>
                                    <PlatformIcon platform={pl} size={10} />
                                  </span>
                                ))}
                                <span className="text-[9px] text-gray-400 ml-1">
                                  {postDate ? new Date(postDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                </span>
                              </div>
                              <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${statusColors[p.status] || 'bg-gray-100 text-gray-500'}`}>
                                {p.status}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )
              ) : (
                /* List view */
                filteredPosts.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <List className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">{t('ws_no_posts', 'No posts found')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredPosts.map((p) => {
                      const postDate = p.scheduled_at || p.published_at || p.created_at;
                      const statusColors: Record<string, string> = {
                        published: 'bg-green-50 text-green-600',
                        scheduled: 'bg-blue-50 text-blue-600',
                        failed: 'bg-red-50 text-red-600',
                        draft: 'bg-gray-100 text-gray-500',
                      };
                      return (
                        <button
                          key={p.id}
                          onClick={() => { setSelectedPostId(p.id); setRightTab('preview'); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${selectedPostId === p.id ? 'bg-blue-50/50' : ''}`}
                        >
                          {/* Thumbnail */}
                          {(p.media_urls && p.media_urls.length > 0) ? (
                            <img src={p.media_urls[0]} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                              <Send className="h-4 w-4 text-gray-300" />
                            </div>
                          )}
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">{p.caption || p.text || p.title || 'No caption'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex -space-x-1">
                                {(p.platforms || []).slice(0, 3).map((pl) => (
                                  <span key={pl} className="w-4 h-4 rounded-full flex items-center justify-center text-white border border-white" style={{ backgroundColor: PLATFORM_COLORS[pl] || '#6b7280' }}>
                                    <PlatformIcon platform={pl} size={8} />
                                  </span>
                                ))}
                              </div>
                              <span className="text-[10px] text-gray-400">
                                {postDate ? new Date(postDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                          </div>
                          {/* Status + engagement */}
                          <div className="flex items-center gap-3 shrink-0">
                            {(p.total_likes !== undefined && p.total_likes > 0) && (
                              <span className="flex items-center gap-0.5 text-[10px] text-gray-400"><Heart className="h-3 w-3" /> {p.total_likes}</span>
                            )}
                            {(p.total_comments !== undefined && p.total_comments > 0) && (
                              <span className="flex items-center gap-0.5 text-[10px] text-gray-400"><MessageCircle className="h-3 w-3" /> {p.total_comments}</span>
                            )}
                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${statusColors[p.status] || 'bg-gray-100 text-gray-500'}`}>
                              {p.status}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </>
        )}

        {/* Feed tab */}
        {centerTab === 'feed' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Feed toolbar */}
            <div className="shrink-0 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-tight text-gray-700">{t('ws_all_posts', 'All Posts')}</span>
                <span className="text-[10px] text-gray-400">({feedTotal || feedPosts.length} {t('ws_posts_text', 'posts')})</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Platform filter */}
                <div className="flex gap-1">
                  <button
                    onClick={() => setFeedPlatformFilter(null)}
                    className={`px-2 py-1 rounded text-[10px] font-semibold ${!feedPlatformFilter ? 'text-white' : 'text-gray-500 bg-gray-100'}`}
                    style={!feedPlatformFilter ? { backgroundColor: theme.hex.primary } : undefined}
                  >
                    {t('ws_all', 'All')}
                  </button>
                  {Array.from(new Set(feedPostsRaw.flatMap(p => p.target_platforms || []))).slice(0, 5).map((pid) => {
                    const pl = PLATFORMS.find(x => x.id === pid);
                    return pl ? (
                      <button
                        key={pid}
                        onClick={() => setFeedPlatformFilter(feedPlatformFilter === pid ? null : pid)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold ${feedPlatformFilter === pid ? 'text-white' : 'text-gray-500 bg-gray-100'}`}
                        style={feedPlatformFilter === pid ? { backgroundColor: PLATFORM_COLORS[pid] || theme.hex.primary } : undefined}
                      >
                        <span className="inline-flex items-center gap-1">{pl.name}</span>
                      </button>
                    ) : null;
                  })}
                </div>
                {/* View toggle */}
                <div className="flex bg-gray-100 rounded p-0.5">
                  <button onClick={() => setFeedViewMode('grid')} className={`p-1 rounded ${feedViewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}>
                    <Grid3X3 className="h-3 w-3 text-gray-500" />
                  </button>
                  <button onClick={() => setFeedViewMode('list')} className={`p-1 rounded ${feedViewMode === 'list' ? 'bg-white shadow-sm' : ''}`}>
                    <List className="h-3 w-3 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {feedLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-600 rounded-full" />
                </div>
              ) : (
                renderFeedContent()
              )}
            </div>
            {/* Pagination */}
            {feedTotal > 20 && (
              <div className="shrink-0 border-t border-gray-200 px-4 py-2 flex items-center justify-between">
                <span className="text-[10px] text-gray-400">Page {feedPage} of {Math.ceil(feedTotal / 20)}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setFeedPage(Math.max(1, feedPage - 1))}
                    disabled={feedPage <= 1}
                    className="px-2 py-1 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setFeedPage(feedPage + 1)}
                    disabled={feedPage >= Math.ceil(feedTotal / 20)}
                    className="px-2 py-1 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Favorites tab — shows user-starred posts */}
        {centerTab === 'favorites' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="shrink-0 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-tight text-gray-700">Favorites</span>
                <span className="text-[10px] text-gray-400">({favoritePosts.length} saved)</span>
              </div>
              <button
                onClick={() => navigate('/compose')}
                className="flex items-center gap-1 px-3 py-1 rounded-full text-white text-[10px] font-semibold"
                style={{ backgroundColor: theme.hex.primary }}
              >
                <Plus className="h-3 w-3" /> New Post
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {favoritesLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-600 rounded-full" />
                </div>
              ) : favoritePosts.length > 0 ? (
                <div className="space-y-2">
                  {favoritePosts.map((p) => {
                    const firstPlat = (p.target_platforms || [])[0];
                    return (
                      <div
                        key={p.id}
                        className={`rounded-lg border p-3 hover:shadow-sm transition-all cursor-pointer ${selectedPostId === p.id ? 'border-current shadow-sm' : 'border-gray-200'}`}
                        style={selectedPostId === p.id ? { borderColor: theme.hex.primary } : undefined}
                        onClick={() => { setSelectedPostId(p.id); setRightTab('preview'); }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedPostId(p.id); setRightTab('preview'); } }}
                      >
                        <div className="flex items-start gap-3">
                          {/* Thumbnail */}
                          {p.media_thumbnail && !brokenImgs.has(p.media_thumbnail) ? (
                            <img src={p.media_thumbnail} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" onError={() => { setBrokenImgs(prev => new Set(prev).add(p.media_thumbnail!)); }} />
                          ) : (
                            <div className="h-14 w-14 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                              <FileEdit className="h-5 w-5 text-gray-300" />
                            </div>
                          )}
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-800 line-clamp-2 leading-snug">{p.text || 'No caption'}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              {firstPlat && (
                                <span className="w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: PLATFORM_COLORS[firstPlat] || '#999' }}>
                                  <PlatformIcon platform={firstPlat} size={10} />
                                </span>
                              )}
                              <span className="text-[10px] text-gray-400">
                                {p.published_at ? new Date(p.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : p.scheduled_at ? `Scheduled ${new Date(p.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Draft'}
                              </span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${p.status === 'published' ? 'bg-green-50 text-green-600' : p.status === 'scheduled' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                                {p.status}
                              </span>
                            </div>
                            {/* Engagement */}
                            <div className="flex items-center gap-3 mt-1.5 text-[9px] text-gray-400">
                              <span className="flex items-center gap-0.5"><Heart className="h-2.5 w-2.5" /> {formatEngagement(p.total_likes)}</span>
                              <span className="flex items-center gap-0.5"><MessageCircle className="h-2.5 w-2.5" /> {formatEngagement(p.total_comments)}</span>
                            </div>
                          </div>
                          {/* Unfavorite button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); favoriteMutation.mutate(p.id); }}
                            className="shrink-0 p-1 hover:bg-yellow-50 rounded transition-colors"
                            title="Remove from favorites"
                          >
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Star className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{t('ws_no_favorites', 'No favorites yet')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('ws_favorites_hint', 'Click the star icon on any post to save it here')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics tab — real API data */}
        {centerTab === 'analytics' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="shrink-0 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
              <span className="text-xs font-bold tracking-tight text-gray-700">{t('ws_analytics_overview', 'Analytics Overview (Last 30 days)')}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => analyticsAPI.sync().then(() => { toast.success(t('ws_syncing_data', 'Syncing engagement data...')); queryClient.invalidateQueries({ queryKey: ['analytics-overview'] }); queryClient.invalidateQueries({ queryKey: ['analytics-top-posts'] }); })} className="text-[10px] font-semibold px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">
                  {t('ws_sync_data', 'Sync Data')}
                </button>
                <button onClick={() => navigate('/analytics')} className="text-[10px] font-semibold flex items-center gap-0.5" style={{ color: theme.hex.primary }}>
                  {t('ws_full_analytics', 'Full Analytics')} <ArrowUpRight className="h-2.5 w-2.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-5">
              {/* Summary stat cards from API overview */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: t('ws_total_likes', 'Total Likes'), value: analyticsOverview?.total_likes ?? 0, icon: Heart, change: analyticsOverview?.trends?.likes_change },
                  { label: t('ws_total_comments', 'Total Comments'), value: analyticsOverview?.total_comments ?? 0, icon: MessageCircle, change: analyticsOverview?.trends?.comments_change },
                  { label: t('ws_total_shares', 'Total Shares'), value: analyticsOverview?.total_shares ?? 0, icon: Share2, change: analyticsOverview?.trends?.shares_change },
                  { label: t('ws_total_reach', 'Total Reach'), value: analyticsOverview?.total_reach ?? 0, icon: Eye, change: analyticsOverview?.trends?.reach_change },
                ].map((stat) => {
                  const Icon = stat.icon;
                  const iconConfig: Record<string, { bg: string; text: string }> = {
                    'Heart': { bg: 'bg-red-50', text: 'text-red-500' },
                    'MessageCircle': { bg: 'bg-blue-50', text: 'text-blue-500' },
                    'Share2': { bg: 'bg-purple-50', text: 'text-purple-500' },
                    'Eye': { bg: 'bg-emerald-50', text: 'text-emerald-500' },
                  };
                  const config = iconConfig[stat.icon.name] || { bg: 'bg-gray-50', text: 'text-gray-500' };
                  return (
                    <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center`}>
                          <Icon className={`h-3.5 w-3.5 ${config.text}`} />
                        </div>
                        {stat.change !== null && stat.change !== undefined && stat.change !== 0 && (
                          <span className={`text-[9px] font-semibold flex items-center gap-0.5 ${stat.change > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {stat.change > 0 ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowUpRight className="h-2.5 w-2.5 rotate-90" />}
                            {Math.abs(stat.change)}%
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-bold tracking-tight text-gray-900">{formatEngagement(stat.value)}</p>
                      <p className="text-[10px] font-semibold text-gray-500 mt-0.5 uppercase tracking-wide">{stat.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Engagement rate card */}
              {analyticsOverview && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{t('ws_engagement_rate', 'Engagement Rate')}</p>
                    <p className="text-2xl font-bold tracking-tight text-gray-900">{(analyticsOverview.engagement_rate || 0).toFixed(2)}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{t('ws_total_posts_label', 'Total Posts')}</p>
                    <p className="text-2xl font-bold tracking-tight text-gray-900">{analyticsOverview.total_posts}</p>
                  </div>
                </div>
              )}

              {/* Platform breakdown from API */}
              <div>
                <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3">{t('ws_posts_by_platform', 'Posts by Platform')}</h3>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  {platformDist.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">{t('ws_no_platform_data', 'No platform data yet')}</p>
                  ) : (
                    <div className="space-y-2.5">
                      {platformDist.map((item) => {
                        const pl = PLATFORMS.find(x => x.id === item.platform);
                        return (
                          <div key={item.platform} className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[7px] shrink-0" style={{ backgroundColor: PLATFORM_COLORS[item.platform] || '#999' }}>
                              {pl?.icon || '?'}
                            </span>
                            <span className="text-[11px] text-gray-700 w-20 shrink-0">{pl?.name || item.platform}</span>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${item.percentage}%`, backgroundColor: PLATFORM_COLORS[item.platform] || theme.hex.primary }} />
                            </div>
                            <span className="text-[10px] font-bold text-gray-600 w-12 text-right">{item.count} ({item.percentage}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Content type performance from API */}
              <div>
                <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3">{t('ws_content_perf', 'Content Type Performance')}</h3>
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {contentPerf.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">{t('ws_no_content_data', 'No content data yet')}</p>
                  ) : (
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-100 text-[10px] text-gray-400">
                          <th className="px-4 py-2 font-semibold">{t('ws_type', 'Type')}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t('ws_posts_text', 'Posts')}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t('ws_avg_likes', 'Avg Likes')}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t('ws_avg_comments', 'Avg Comments')}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t('ws_avg_reach', 'Avg Reach')}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t('ws_engagement', 'Engagement')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contentPerf.map((item) => (
                          <tr key={item.content_type} className="border-b border-gray-50">
                            <td className="px-4 py-2 text-xs font-semibold text-gray-700 capitalize">{item.content_type}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 text-center">{item.total_posts}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 text-center">{formatEngagement(Math.round(item.avg_likes))}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 text-center">{formatEngagement(Math.round(item.avg_comments))}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 text-center">{formatEngagement(Math.round(item.avg_reach))}</td>
                            <td className="px-3 py-2 text-xs text-center font-semibold" style={{ color: theme.hex.primary }}>{item.engagement_rate.toFixed(2)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Top performing posts from API */}
              <div>
                <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3">{t('ws_top_posts', 'Top Performing Posts')}</h3>
                <div className="space-y-2">
                  {topPosts.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                      <p className="text-xs text-gray-400">No post performance data yet</p>
                    </div>
                  ) : (
                    topPosts.slice(0, 5).map((p, idx) => {
                      return (
                        <div key={p.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
                          <span className="text-sm font-bold text-gray-300 w-5 text-center">{idx + 1}</span>
                          {p.media_thumbnail && !brokenImgs.has(p.media_thumbnail) ? (
                            <img src={p.media_thumbnail} alt="" className="h-10 w-10 rounded object-cover shrink-0" onError={() => { setBrokenImgs(prev => new Set(prev).add(p.media_thumbnail!)); }} />
                          ) : (
                            <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center shrink-0">
                              <FileEdit className="h-4 w-4 text-gray-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-800 line-clamp-1">{p.text || 'No caption'}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {p.platforms?.[0] && <span style={{ color: PLATFORM_COLORS[p.platforms[0]] || '#999' }}><PlatformIcon platform={p.platforms[0]} size={10} /></span>}
                              <span className="text-[9px] text-gray-400">
                                {p.published_at ? new Date(p.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Draft'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-gray-500 shrink-0">
                            <span className="flex items-center gap-0.5"><Heart className="h-3 w-3" /> {formatEngagement(p.total_likes || 0)}</span>
                            <span className="flex items-center gap-0.5"><MessageCircle className="h-3 w-3" /> {formatEngagement(p.total_comments || 0)}</span>
                            <span className="flex items-center gap-0.5"><Share2 className="h-3 w-3" /> {formatEngagement(p.total_shares || 0)}</span>
                            <span className="flex items-center gap-0.5"><Eye className="h-3 w-3" /> {formatEngagement(p.total_reach || 0)}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Best posting times from API */}
              {bestTimesData && (
                <div>
                  <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3">Best Posting Times (AI Recommended)</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    {(() => { const bt_arr = Array.isArray(bestTimesData.best_times) ? bestTimesData.best_times : Array.isArray(bestTimesData) ? bestTimesData : []; return bt_arr.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center">Not enough data for recommendations yet</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {bt_arr.slice(0, 6).map((bt: { day: string; hour: number; engagement_rate: number; sample_size: number }, idx: number) => (
                          <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                            <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <div>
                              <p className="text-[11px] font-semibold text-gray-700">{bt.day} at {bt.hour}:00</p>
                              <p className="text-[9px] text-gray-400">{bt.engagement_rate.toFixed(1)}% engagement · {bt.sample_size} posts</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ); })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings tab — workspace preferences */}
        {centerTab === 'settings' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="shrink-0 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
              <span className="text-xs font-bold tracking-tight text-gray-700">{t('ws_workspace_settings', 'Workspace Settings')}</span>
              <button onClick={() => navigate('/settings')} className="text-[10px] font-semibold flex items-center gap-0.5" style={{ color: theme.hex.primary }}>
                {t('ws_all_settings', 'All Settings')} <ArrowUpRight className="h-2.5 w-2.5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 max-w-2xl">
              <div className="space-y-5">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-xs font-semibold text-amber-900">
                    {t('ws_settings_readonly_title', 'Workspace settings are managed from Settings.')}
                  </p>
                  <p className="mt-1 text-[11px] text-amber-700">
                    {t('ws_settings_readonly_desc', 'This tab is a quick summary so values do not silently appear editable here.')}
                  </p>
                </div>

                {/* Posting Preferences */}
                <div>
                  <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3 flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" style={{ color: theme.hex.primary }} />
                    {t('ws_posting_preferences', 'Posting Preferences')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t('ws_default_post_time', 'Default post time')}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t('ws_default_post_time_desc', 'Auto-fill schedule time for new posts')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50"
                      >
                        {t('ws_configure', 'Configure')}
                      </button>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t('ws_auto_schedule_gap', 'Auto-schedule gap')}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t('ws_auto_schedule_gap_desc', 'Minimum hours between auto-scheduled posts')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/queue')}
                        className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50"
                      >
                        {t('ws_open_queue', 'Open Queue')}
                      </button>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t('ws_calendar_day', 'Calendar start day')}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t('ws_calendar_day_desc', 'First day of the week in calendar view')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-[10px] font-semibold px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50"
                      >
                        {t('ws_configure', 'Configure')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Notifications */}
                <div>
                  <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3 flex items-center gap-1.5">
                    <Bell className="h-3.5 w-3.5" style={{ color: theme.hex.primary }} />
                    {t('ws_notifications', 'Notifications')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                    {[
                      { label: t('ws_post_published', 'Post published'), desc: t('ws_published_desc', 'Notify when a scheduled post is published'), defaultOn: true },
                      { label: t('ws_post_failed', 'Post failed'), desc: t('ws_failed_desc', 'Notify when publishing fails'), defaultOn: true },
                      { label: t('ws_new_comments', 'New comments'), desc: t('ws_comments_desc', 'Notify when posts receive comments'), defaultOn: false },
                      { label: t('ws_milestones', 'Engagement milestones'), desc: t('ws_milestones_desc', 'Notify on reach milestones (100, 500, 1K likes)'), defaultOn: false },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-700">{item.label}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{item.desc}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate('/settings')}
                          className="text-gray-400 hover:text-gray-600"
                          title={t('ws_manage_notifications', 'Manage notifications')}
                        >
                          {item.defaultOn ? <ToggleRight className="h-5 w-5" style={{ color: theme.hex.primary }} /> : <ToggleLeft className="h-5 w-5" />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Default Content Settings */}
                <div>
                  <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3 flex items-center gap-1.5">
                    <Hash className="h-3.5 w-3.5" style={{ color: theme.hex.primary }} />
                    {t('ws_default_content', 'Default Content Settings')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t('ws_auto_hashtags', 'Auto-add hashtags')}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t('ws_hashtags_desc', 'Automatically append saved hashtag groups')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-gray-400 hover:text-gray-600"
                        title={t('ws_manage_content_defaults', 'Manage content defaults')}
                      >
                        <ToggleLeft className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t('ws_first_comment', 'Default first comment')}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t('ws_first_comment_desc', 'Add hashtags as first comment on Instagram')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-gray-400 hover:text-gray-600"
                        title={t('ws_manage_content_defaults', 'Manage content defaults')}
                      >
                        <ToggleLeft className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{t('ws_shorten_links', 'Auto-shorten links')}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t('ws_shorten_desc', 'Automatically shorten URLs using PlugD Short Links')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-gray-400 hover:text-gray-600"
                        title={t('ws_manage_content_defaults', 'Manage content defaults')}
                      >
                        <ToggleRight className="h-5 w-5" style={{ color: theme.hex.primary }} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Connected Accounts Summary */}
                <div>
                  <h3 className="text-[13px] font-bold tracking-tight text-gray-700 mb-3 flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" style={{ color: theme.hex.primary }} />
                    {t('ws_connected_accounts', 'Connected Accounts')} ({accounts.length})
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    {accounts.length > 0 ? (
                      <div className="space-y-2">
                        {accounts.map((a) => {
                          const pl = PLATFORMS.find(x => x.id === a.platform);
                          return (
                            <div key={a.id} className="flex items-center gap-2.5">
                              <AccountAvatar
                                src={a.avatar_url}
                                name={a.display_name}
                                username={a.username}
                                size={28}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold text-gray-700 truncate">{a.display_name || a.username}</p>
                                <p className="text-[9px] text-gray-400">{pl?.name || a.platform}</p>
                              </div>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                {a.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-2">{t('ws_no_accounts', 'No accounts connected')}</p>
                    )}
                    <button
                      onClick={() => navigate('/accounts')}
                      className="mt-3 w-full text-center text-[10px] font-semibold py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                      style={{ color: theme.hex.primary }}
                    >
                      {t('ws_manage_accounts', 'Manage Accounts')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right: Preview / Comments / Activity */}
      <PreviewPanel
        post={selectedPost}
        accounts={accounts}
        theme={theme}
        activeTab={rightTab}
        onTabChange={setRightTab}
        onToggleFavorite={selectedPostId ? () => favoriteMutation.mutate(selectedPostId) : undefined}
      />
    </div>
  );
}
